import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type BadgeKind =
  | 'MANUAL'
  | 'POINTS_THRESHOLD'
  | 'RECEIPTS_COUNT'
  | 'CHECKINS_COUNT'
  | 'ORDERS_COUNT'
  | 'CUSTOM';

export interface BadgeDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  icon: string;
  kind: BadgeKind;
  threshold: number | null;
  sortOrder: number;
  isActive: boolean;
  unlockedCount?: number;
}

export interface BadgeUnlockDto {
  userId: string;
  displayName: string | null;
  email: string;
  fanCardCode: string;
  unlockedAt: string;
  grantedBy: string | null;
  reason: string | null;
}

export interface CreateBadgeInput {
  code: string;
  name: string;
  description?: string;
  icon: string;
  kind?: BadgeKind;
  threshold?: number;
  sortOrder?: number;
  isActive?: boolean;
}

export type UpdateBadgeInput = Partial<CreateBadgeInput>;

const QK = {
  list: ['badges'] as const,
  unlocks: (id: string) => ['badges', id, 'unlocks'] as const,
};

export function useBadges() {
  return useQuery({
    queryKey: QK.list,
    queryFn: async () => {
      const res = await apiClient.get<BadgeDto[]>('/admin/badges');
      return res.data;
    },
  });
}

export function useBadgeUnlocks(badgeId: string | null) {
  return useQuery({
    queryKey: QK.unlocks(badgeId ?? ''),
    enabled: Boolean(badgeId),
    queryFn: async () => {
      const res = await apiClient.get<BadgeUnlockDto[]>(`/admin/badges/${badgeId}/unlocks`);
      return res.data;
    },
  });
}

export function useCreateBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateBadgeInput) => {
      const res = await apiClient.post<BadgeDto>('/admin/badges', input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useUpdateBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateBadgeInput }) => {
      const res = await apiClient.patch<BadgeDto>(`/admin/badges/${id}`, input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useDeleteBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/badges/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useGrantBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      badgeId,
      userId,
      reason,
    }: {
      badgeId: string;
      userId: string;
      reason?: string;
    }) => {
      const res = await apiClient.post(`/admin/badges/${badgeId}/grant/${userId}`, {
        reason,
      });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.list });
      qc.invalidateQueries({ queryKey: QK.unlocks(vars.badgeId) });
    },
  });
}

export function useRevokeBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ badgeId, userId }: { badgeId: string; userId: string }) => {
      await apiClient.delete(`/admin/badges/${badgeId}/grant/${userId}`);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.list });
      qc.invalidateQueries({ queryKey: QK.unlocks(vars.badgeId) });
    },
  });
}
